/**
 * Certmgr — Certifikáty a CA infrastruktura
 * Root CA (Docker), Intermediate CA, Node certs, Let's Encrypt
 */
import type { EffectiveScope, WidgetRenderProps } from '../../../widgets/types';
import { callWidgetTool } from '../../../widgets/scope';
import { useMeshPeers } from '../../../widgets/NodePicker';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../../lib/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { downloadBase64 } from '../../../lib/download';
import { StatCard, StatsGrid } from '../../../components/ui/Stats';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../components/ui/Card';
import { Tab, TabList, TabPanel, Tabs } from '../../../components/ui/Tabs';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { EmptyState } from '../../../components/ui/EmptyState';
import { StatusDot } from '../../../components/ui/StatusDot';
import { Modal } from '../../../components/ui/Modal';
import { SearchInput } from '../../../components/ui/SearchInput';
import { Badge } from '../../../components/ui/Badge';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Download,
  FileText,
  Globe,
  Info,
  Key,
  Loader2,
  Lock,
  Plus,
  RefreshCw,
  Server,
  Shield,
  Terminal,
  Trash2,
} from 'lucide-react';


let _widgetScope: EffectiveScope = { node: null, identity: null };
export function setWidgetScope(scope: EffectiveScope) {
  _widgetScope = scope;
}
function callMcp<T = unknown>(
  name: string,
  args: Record<string, unknown> = {},
  opts?: { silent?: boolean; label?: string },
) {
  return callWidgetTool(name, args, _widgetScope, opts) as Promise<T>;
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface Cert {
  id: number;
  serial: string;
  common_name: string;
  cert_type: 'node' | 'service' | 'intermediate' | 'le';
  issued_to: string;
  issued_at: string;
  expires_at: string;
  revoked_at: string | null;
  revoke_reason: string;
}

interface CAStatus {
  ok: boolean;
  rootCa: {
    ready: boolean;
    dir: string;
    image: string;
  };
  intermediateCa: {
    ready: boolean;
    dir: string;
    expiresAt?: string;
  };
  certs: {
    total: number;
    active: number;
    revoked: number;
    expired: number;
  };
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CERT_TYPE_LABELS: Record<string, string> = {
  node: 'Node',
  service: 'Service',
  intermediate: 'Intermediate CA',
  le: "Let's Encrypt",
};

const CERT_TYPE_COLORS: Record<string, string> = {
  node: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
  service: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
  intermediate: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
  le: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
};

const STATUS_LABELS: Record<string, { key: string; fallback: string }> = {
  active: { key: 'certmgr.statusActive', fallback: 'Aktivní' },
  revoked: { key: 'certmgr.statusRevoked', fallback: 'Revokováno' },
  expired: { key: 'certmgr.statusExpired', fallback: 'Expirovalo' },
};

const STATUS_COLORS: Record<string, string> = {
  active: 'text-emerald-400',
  revoked: 'text-red-400',
  expired: 'text-amber-400',
};

// ─── StatusBadge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const mapped = STATUS_LABELS[status];
  const label = mapped ? t(mapped.key, mapped.fallback) : status;
  const color = STATUS_COLORS[status] || 'text-gray-500';
  return <span className={`text-xs font-medium ${color}`}>{label}</span>;
}

// ─── TypeBadge ───────────────────────────────────────────────────────────────

function TypeBadge({ type }: { type: string }) {
  const color = CERT_TYPE_COLORS[type] || 'text-gray-500 bg-gray-800';
  const label = CERT_TYPE_LABELS[type] || type;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${color}`}
    >
      {label}
    </span>
  );
}

// ─── CAStatusCard ────────────────────────────────────────────────────────────

function CAStatusCard(
  { label, status, dir, expiresAt, absent }: {
    label: string;
    status: boolean;
    dir: string;
    expiresAt?: string;
    /** CA na tomto uzlu legitimně není (drží ji nadřazená autorita) — šedě, ne error. */
    absent?: boolean;
  },
) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>
        <CardTitle icon={status ? Shield : AlertCircle} className='text-sm'>
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent className='space-y-3'>
        <div className='flex items-center justify-between'>
          <span className='text-xs text-gray-500'>Stav</span>
          {absent
            ? <span className='text-[11px] text-gray-500'>{t('certmgr.mimoTentoUzel', 'mimo tento uzel')}</span>
            : <StatusDot status={status ? 'running' : 'error'} withLabel />}
        </div>
        <div className='flex items-center justify-between'>
          <span className='text-xs text-gray-500'>Cesta</span>
          <code className='text-[10px] font-mono text-gray-400 bg-gray-800 px-1.5 py-0.5 rounded'>
            {dir}
          </code>
        </div>
        {expiresAt && (
          <div className='flex items-center justify-between'>
            <span className='text-xs text-gray-500'>Expirace</span>
            <span className='text-xs font-medium text-gray-300'>{expiresAt}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── CertTable ───────────────────────────────────────────────────────────────

function CertTable({ certs, onRevoke }: { certs: Cert[]; onRevoke: (serial: string) => void }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  // MCP může vrátit ne-pole → .filter crash (ui_crash /core/certmgr)
  const list = Array.isArray(certs) ? certs : [];

  const filtered = list.filter((c) =>
    (c.common_name ?? '').toLowerCase().includes(search.toLowerCase()) ||
    (c.serial ?? '').toLowerCase().includes(search.toLowerCase()) ||
    (c.issued_to ?? '').toLowerCase().includes(search.toLowerCase())
  );

  if (list.length === 0) {
    return (
      <EmptyState
        icon={<Shield size={24} />}
        title={t('certmgr.zadneCertifikaty')}
        description={t('certmgr.zatimNebylyPodepsanyZadne')}
      />
    );
  }

  return (
    <div className='space-y-4'>
      <SearchInput
        value={search}
        onChange={setSearch}
        placeholder={t('certmgr.hledatCnSerialIssued')}
        resultCount={filtered.length}
        totalCount={list.length}
      />

      <div className='rounded-xl border border-gray-800 overflow-hidden bg-gray-900/20'>
        {/* Desktop table */}
        <table className='w-full text-left text-sm hidden md:table'>
          <thead className='bg-gray-900/40 border-b border-gray-800'>
            <tr>
              <th className='px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider'>
                Typ
              </th>
              <th className='px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider'>
                {t('certmgr.commonName')}
              </th>
              <th className='px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider'>
                Serial
              </th>
              <th className='px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider'>
                {t('certmgr.vydanoPro')}
              </th>
              <th className='px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider'>
                Platnost
              </th>
              <th className='px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider'>
                Stav
              </th>
              <th className='px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right'>
                Akce
              </th>
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-800'>
            {filtered.map((cert) => (
              <tr key={cert.id} className='hover:bg-gray-800/30 transition-colors'>
                <td className='px-4 py-3'>
                  <TypeBadge type={cert.cert_type} />
                </td>
                <td className='px-4 py-3 font-medium text-white'>
                  {cert.common_name}
                </td>
                <td className='px-4 py-3 font-mono text-xs text-gray-400'>
                  {cert.serial}
                </td>
                <td className='px-4 py-3 text-gray-300'>
                  {cert.issued_to || '—'}
                </td>
                <td className='px-4 py-3'>
                  <div className='flex flex-col gap-0.5'>
                    <span className='text-xs text-gray-400'>
                      {new Date(cert.expires_at).toLocaleDateString('cs-CZ')}
                    </span>
                    {cert.revoked_at && (
                      <span className='text-[10px] text-red-400'>
                        Revokováno: {new Date(cert.revoked_at).toLocaleDateString('cs-CZ')}
                      </span>
                    )}
                  </div>
                </td>
                <td className='px-4 py-3'>
                  {cert.revoked_at
                    ? <StatusBadge status='revoked' />
                    : new Date(cert.expires_at) < new Date()
                    ? <StatusBadge status='expired' />
                    : <StatusBadge status='active' />}
                </td>
                <td className='px-4 py-3 text-right'>
                  {!cert.revoked_at && (
                    <Button
                      variant='danger'
                      size='sm'
                      icon={<Trash2 size={10} />}
                      onClick={() => onRevoke(cert.serial)}
                    >
                      Revokovat
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Phone cards */}
        <div className='md:hidden space-y-2 p-2'>
          {filtered.map((cert) => (
            <div
              key={cert.id}
              className='rounded-xl border border-gray-800 bg-gray-950/50 p-3 space-y-2'
            >
              <div className='flex items-start justify-between gap-2'>
                <div className='min-w-0'>
                  <div className='font-medium text-white text-sm truncate'>{cert.common_name}</div>
                  <div className='font-mono text-[11px] text-gray-500 truncate'>{cert.serial}</div>
                </div>
                {cert.revoked_at
                  ? <StatusBadge status='revoked' />
                  : new Date(cert.expires_at) < new Date()
                  ? <StatusBadge status='expired' />
                  : <StatusBadge status='active' />}
              </div>
              <div className='flex flex-wrap items-center gap-2 text-[11px] text-gray-400'>
                <TypeBadge type={cert.cert_type} />
                <span>{cert.issued_to || '—'}</span>
                <span className='ml-auto'>
                  {new Date(cert.expires_at).toLocaleDateString('cs-CZ')}
                </span>
              </div>
              {!cert.revoked_at && (
                <Button
                  variant='danger'
                  size='sm'
                  icon={<Trash2 size={10} />}
                  onClick={() => onRevoke(cert.serial)}
                  className='w-full'
                >
                  Revokovat
                </Button>
              )}
            </div>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className='p-8 text-center text-gray-500 text-sm'>
            {t('certmgr.zadneCertifikatyNenalezeny')}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

// ── DNS Providers Panel ──────────────────────────────────────────────────────

function DnsProvidersPanel() {
  const { t } = useTranslation();
  // Read-only view nad acm.dns_providers (zdroj pravdy = dns modul).
  // Lifecycle (add/edit/delete/test) je v /modules/dns → Providers tab.

  const { data: resp, isLoading } = useQuery<{
    ok: boolean;
    plugins: string[];
    providers: Array<{
      plugin: string;
      dns_type: string | null;
      dns_provider_name: string | null;
      configured: boolean;
    }>;
    dns_module_providers: number;
  }>({
    queryKey: ['certmgr-dns-providers'],
    queryFn: () => callMcp('certmgr.dns_providers', {}),
    refetchInterval: 30_000,
  });

  // Issue cert form
  const [issueDomain, setIssueDomain] = useState('');
  const [issueEmail, setIssueEmail] = useState('');
  const [issueChallenge, setIssueChallenge] = useState('standalone');
  const [issueWildcard, setIssueWildcard] = useState(false);

  const issueMut = useMutation({
    mutationFn: () =>
      callMcp('certmgr.le', {
            action: 'issue',
            domain: issueDomain,
            email: issueEmail,
            challenge: issueChallenge,
            wildcard: issueWildcard,
          }),
    onSuccess: () => {
      toast.success(t('certmgr.certIssued', 'Certifikát pro {{domain}} vytvořen', {
        domain: `${issueWildcard ? '*.' : ''}${issueDomain}`,
      }));
      setIssueDomain('');
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const inp =
    'w-full bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500';

  if (isLoading) {
    return (
      <div className='py-8 text-center text-gray-500'>
        <Loader2 size={16} className='animate-spin inline mr-2' />{t('certmgr.nacitam')}
      </div>
    );
  }

  const providerRows = Array.isArray(resp?.providers) ? resp!.providers! : [];
  const dnsModuleCount = resp?.dns_module_providers ?? 0;

  return (
    <div className='space-y-4'>
      {/* Info banner */}
      <div className='flex items-center justify-between bg-blue-500/5 border border-blue-500/20 rounded-lg px-4 py-2.5 text-sm'>
        <div className='flex items-center gap-2 text-blue-300'>
          <Info size={14} />
          <span>
            {t('certmgr.dnsCredsManagedIn', 'DNS provider credentials se spravují v')}{' '}
            <a href='/modules/dns' className='underline hover:text-blue-200'>
              {t('certmgr.dnsModuluProviders')}
            </a>
            {t('certmgr.dnsCredsReadonlyHint', '. Tady je read-only přehled co je k dispozici pro DNS-01 ACME challenge.')}
            {t('certmgr.dnsProviderCount', '({{count}} providerů v DNS modulu.)', { count: dnsModuleCount })}
          </span>
        </div>
        <a
          href='/modules/dns'
          className='text-xs px-3 py-1 rounded bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 ml-3'
        >
          {t('certmgr.manage', 'Spravovat')}
        </a>
      </div>

      {/* Plugin status */}
      <Card>
        <CardHeader>
          <CardTitle icon={Shield}>{t('certmgr.certbotDnsPluginyMapping')}</CardTitle>
        </CardHeader>
        <CardContent>
          {providerRows.length === 0
            ? (
              <p className='text-xs text-gray-500'>
                {t('certmgr.zadneDnsPluginyNainstalovane', 'Žádné DNS pluginy nainstalované. Pro DNS-01 challenge nainstaluj')}{' '}
                <code>certbot-dns-cloudflare</code> {t('certmgr.nebo', 'nebo')} <code>certbot-dns-route53</code>.
              </p>
            )
            : (
              <div className='space-y-2'>
                {providerRows.map((p) => (
                  <div
                    key={p.plugin}
                    className='flex items-center justify-between p-2.5 bg-gray-900/40 rounded-lg'
                  >
                    <div className='flex items-center gap-3'>
                      <div
                        className={`w-2 h-2 rounded-full ${
                          p.configured ? 'bg-emerald-400' : 'bg-gray-600'
                        }`}
                      />
                      <div>
                        <span className='text-sm font-mono text-white'>{p.plugin}</span>
                        {p.dns_type && (
                          <span className='text-[10px] text-gray-500 ml-2'>
                            {t('certmgr.dnsType')}<code>{p.dns_type}</code>
                          </span>
                        )}
                      </div>
                    </div>
                    {p.configured
                      ? (
                        <Badge variant='success'>
                          Nakonfigurováno ({p.dns_provider_name})
                        </Badge>
                      )
                      : (
                        <div className='flex items-center gap-2'>
                          <Badge variant='default'>{t('certmgr.zadnaKonfigurace')}</Badge>
                          {p.dns_type && (
                            <a
                              href='/modules/dns'
                              className='text-[10px] px-2 py-0.5 rounded bg-blue-500/10 text-blue-400 hover:bg-blue-500/20'
                            >
                              {t('certmgr.nastavitVDnsModulu')}
                            </a>
                          )}
                        </div>
                      )}
                  </div>
                ))}
              </div>
            )}
        </CardContent>
      </Card>

      {/* Issue cert with DNS challenge */}
      <Card>
        <CardHeader>
          <CardTitle icon={Plus}>{t('certmgr.vydatCertifikatDnsChallenge')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-2 gap-3'>
            <div>
              <label className='text-xs text-gray-500 mb-1 block'>{t('certmgr.domena')}</label>
              <input
                value={issueDomain}
                onChange={(e) => setIssueDomain(e.target.value)}
                placeholder='example.com'
                className={inp + ' font-mono'}
              />
            </div>
            <div>
              <label className='text-xs text-gray-500 mb-1 block'>Email</label>
              <input
                value={issueEmail}
                onChange={(e) => setIssueEmail(e.target.value)}
                placeholder={t('certmgr.adminExampleCom')}
                className={inp}
              />
            </div>
          </div>
          <div className='grid grid-cols-2 gap-3 mt-3'>
            <div>
              <label className='text-xs text-gray-500 mb-1 block'>{t('certmgr.challengeMetoda')}</label>
              <Select
                value={issueChallenge}
                onChange={(e) => setIssueChallenge(e.target.value)}
              >
                <option value='standalone'>{t('certmgr.standalonePort80')}</option>
                {providerRows
                  .filter((p) => p && p.configured)
                  .map((p) => (
                    <option key={p.plugin} value={p.plugin}>
                      {p.plugin}{' '}
                      {p.dns_provider_name ? `(${p.dns_provider_name})` : ''}
                    </option>
                  ))}
              </Select>
            </div>
            <div className='flex items-end gap-3'>
              <label className='flex items-center gap-2 cursor-pointer pb-2'>
                <input
                  type='checkbox'
                  checked={issueWildcard}
                  onChange={(e) => setIssueWildcard(e.target.checked)}
                />
                <span className='text-sm text-gray-300'>{t('certmgr.wildcardDomain')}</span>
              </label>
            </div>
          </div>
          {issueWildcard && issueChallenge === 'standalone' && (
            <div className='mt-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-300'>
              {t('certmgr.wildcardVyzadujeDns01')}
            </div>
          )}
          <div className='flex justify-end mt-4'>
            <Button
              variant='primary'
              loading={issueMut.isPending}
              onClick={() => issueMut.mutate()}
              disabled={!issueDomain || !issueEmail || (issueWildcard && issueChallenge === 'standalone')}
            >
              <Globe size={13} /> {t('certmgr.vydatCertifikat')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


// ─── Cert Store Tab — universal cert library (import, generate, scan, push to NPM) ───────

interface StoreCert {
  id: string;
  domain: string;
  alt_names: string[];
  provider: string;
  status: string;
  expires_at: string | null;
  auto_renew: boolean;
  managed_by: string | null;
}

const PROVIDER_CLR: Record<string, string> = {
  'lets-encrypt': 'text-emerald-400 bg-emerald-500/10',
  'root-ca': 'text-blue-400 bg-blue-500/10',
  'imported': 'text-purple-400 bg-purple-500/10',
  'npm-managed': 'text-amber-400 bg-amber-500/10',
  'acme': 'text-cyan-400 bg-cyan-500/10',
};

function certExpiry(expires: string | null) {
  if (!expires) return { label: '—', cls: 'text-gray-500 bg-gray-800' };
  const days = Math.ceil((new Date(expires).getTime() - Date.now()) / 86400000);
  if (days < 0) return { label: i18n.t('certmgr.statusExpired', 'Expirovaný'), cls: 'text-red-400 bg-red-500/10' };
  if (days < 14) return { label: `${days}d`, cls: 'text-amber-400 bg-amber-500/10' };
  return { label: `${days}d`, cls: 'text-emerald-400 bg-emerald-500/10' };
}

function CertStoreTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  // Load — přes MCP tool certmgr.cert_store action='list'
  const {
    data: storeResp,
    isLoading,
    isError,
    error: storeError,
    refetch,
  } = useQuery<{
    ok: boolean;
    certificates?: StoreCert[];
    summary?: { total: number; active: number; expiring_soon: number };
  }>({
    queryKey: ['certmgr-store'],
    queryFn: () =>
      callMcp<{
        ok: boolean;
        certificates?: StoreCert[];
        summary?: { total: number; active: number; expiring_soon: number };
      }>('certmgr.cert_store', { action: 'list' }),
    staleTime: 30_000,
  });

  // Guard: MCP unwrap někdy vrátí ne-array → .map/.filter by spadlo
  const certs = Array.isArray(storeResp?.certificates) ? storeResp!.certificates! : [];
  const summary = storeResp?.summary;

  // Actions
  const scanMut = useMutation({
    mutationFn: () => callMcp('certmgr.cert_store', { action: 'scan' }),
    onSuccess: () => {
      toast.success(t('certmgr.scanDokoncen'));
      refetch();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const renewMut = useMutation({
    mutationFn: () => callMcp('certmgr.cert_store', { action: 'renew' }),
    onSuccess: () => {
      toast.success(t('certmgr.renewalSpusten'));
      refetch();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  // Import ze souboru
  const [importOpen, setImportOpen] = useState(false);
  const [importCert, setImportCert] = useState('');
  const [importKey, setImportKey] = useState('');
  const [importChain, setImportChain] = useState('');
  const [importLabel, setImportLabel] = useState('');
  const [importOverwrite, setImportOverwrite] = useState(false);

  const importMut = useMutation({
    mutationFn: () =>
      callMcp<{
        ok: boolean;
        output?: string;
        error?: string;
        domain?: string;
        expires_at?: string;
      }>('certmgr.cert_import', {
        cert_pem: importCert,
        key_pem: importKey,
        chain_pem: importChain || undefined,
        label: importLabel || undefined,
        overwrite: importOverwrite,
      }),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success(t('certmgr.certImported', 'Cert pro {{domain}} importován', { domain: r.domain }));
        setImportOpen(false);
        setImportCert('');
        setImportKey('');
        setImportChain('');
        setImportLabel('');
        setImportOverwrite(false);
        refetch();
        qc.invalidateQueries({ queryKey: ['certmgr-certs'] });
      } else {
        toast.error(r.error ?? i18n.t('certmgr.importSelhal', 'Import selhal'));
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  async function handleImportFile(
    e: React.ChangeEvent<HTMLInputElement>,
    setter: (v: string) => void,
  ) {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setter(text);
  }

  // Push to NPM
  const toNpmMut = useMutation({
    mutationFn: (cert_id: string) =>
      callMcp<{ ok: boolean; error?: string; domain?: string; npm_cert_id?: number }>(
        'npm.import_cert_from_store',
        { cert_id },
      ),
    onSuccess: (r) => {
      if (r.ok) toast.success(`${r.domain} → NPM cert ID ${r.npm_cert_id}`);
      else toast.error(r.error ?? i18n.t('certmgr.npmUploadSelhal', 'NPM upload selhal'));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className='space-y-4'>
      {/* Header card s actions */}
      <Card>
        <CardHeader>
          <CardTitle icon={Shield}>
            Cert Store
            {summary && (
              <span className='ml-3 text-xs font-normal text-gray-400'>
                {summary.total} celkem · {summary.active} aktivních
                {summary.expiring_soon > 0 && (
                  <span className='text-amber-400 ml-1'>
                    · {summary.expiring_soon} expiruje brzo
                  </span>
                )}
              </span>
            )}
          </CardTitle>
          <div className='flex items-center gap-2'>
            <Button
              variant='primary'
              size='sm'
              onClick={() => setImportOpen(true)}
            >
              <Plus size={13} /> {t('certmgr.importZeSouboru')}
            </Button>
            <Button
              variant='ghost'
              size='sm'
              loading={scanMut.isPending}
              onClick={() => scanMut.mutate()}
              title={t('certmgr.naskenujeEtcLetsencryptEtc')}
            >
              {t('certmgr.scanDisk')}
            </Button>
            <Button
              variant='warning'
              size='sm'
              loading={renewMut.isPending}
              onClick={() => renewMut.mutate()}
            >
              {t('certmgr.obnovitExpirujici')}
            </Button>
            <Button variant='ghost' size='sm' onClick={() => refetch()} loading={isLoading}>
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className='p-0'>
          {isError ? (
            <div className='text-center py-12 text-red-400 text-sm px-4'>
              {(storeError as Error)?.message ?? t('certmgr.nacitaniSelhalo', 'Načtení Cert Store selhalo')}
            </div>
          ) : certs.length === 0 ? (
            <div className='text-center py-12 text-gray-500 px-4'>
              <Shield size={32} className='mx-auto mb-3 opacity-30' />
              <p className='text-sm mb-1'>{t('certmgr.certStoreJePrazdny')}</p>
              <p className='text-xs max-w-lg mx-auto leading-relaxed'>
                {t(
                  'certmgr.certStoreEmptyHint',
                  'Cert Store drží veřejné/LE/importované certifikáty (acm.certificates). Mesh node/client certy z interní CA jsou v záložce „Mesh certifikáty“, ne tady.',
                )}
              </p>
              <p className='text-xs mt-3'>
                {t('certmgr.importZeSouboru')} · {t('certmgr.scanDisk')} ( /etc/letsencrypt, … )
              </p>
            </div>
          ) : (
            <>
              <table className='w-full hidden md:table'>
                <thead>
                  <tr className='border-b border-gray-800'>
                    <th className='text-left py-2.5 px-4 text-xs text-gray-400'>{t('certmgr.domena')}</th>
                    <th className='text-left py-2.5 px-4 text-xs text-gray-400'>Provider</th>
                    <th className='text-left py-2.5 px-4 text-xs text-gray-400'>Status</th>
                    <th className='text-left py-2.5 px-4 text-xs text-gray-400'>Expiruje</th>
                    <th className='text-left py-2.5 px-4 text-xs text-gray-400'>Spravuje</th>
                    <th className='text-right py-2.5 px-4 text-xs text-gray-400'>Akce</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-800/30'>
                  {certs.map((c) => {
                    const exp = certExpiry(c.expires_at);
                    return (
                      <tr key={c.id} className='hover:bg-gray-800/20'>
                        <td className='py-2.5 px-4'>
                          <span className='text-sm font-mono text-white'>{c.domain}</span>
                          {c.auto_renew && (
                            <span className='text-[9px] text-blue-400 ml-1.5'>auto</span>
                          )}
                          {Array.isArray(c.alt_names) && c.alt_names.length > 1 && (
                            <span
                              className='text-[10px] text-gray-500 ml-1.5'
                              title={c.alt_names.join(', ')}
                            >
                              +{c.alt_names.length - 1} SAN
                            </span>
                          )}
                        </td>
                        <td className='py-2.5 px-4'>
                          <span
                            className={`text-xs px-2 py-0.5 rounded-lg ${
                              PROVIDER_CLR[c.provider] ?? 'bg-gray-800 text-gray-400'
                            }`}
                          >
                            {c.provider}
                          </span>
                        </td>
                        <td className='py-2.5 px-4'>
                          <span
                            className={`text-xs ${
                              c.status === 'active' ? 'text-emerald-400' : 'text-red-400'
                            }`}
                          >
                            {c.status}
                          </span>
                        </td>
                        <td className='py-2.5 px-4'>
                          <span className={`text-xs px-2 py-0.5 rounded-lg ${exp.cls}`}>
                            {exp.label}
                          </span>
                        </td>
                        <td className='py-2.5 px-4 text-xs text-gray-500'>
                          {c.managed_by ?? '—'}
                        </td>
                        <td className='py-2.5 px-4 text-right'>
                          <Button
                            variant='ghost'
                            size='sm'
                            disabled={c.status !== 'active'}
                            loading={toNpmMut.isPending && toNpmMut.variables === c.id}
                            onClick={() => toNpmMut.mutate(c.id)}
                            title={t('certmgr.uploadneCertDoNpm')}
                          >
                            {t('certmgr.npm')}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className='md:hidden space-y-2 p-2'>
                {certs.map((c) => {
                  const exp = certExpiry(c.expires_at);
                  return (
                    <div
                      key={c.id}
                      className='rounded-xl border border-gray-800 bg-gray-950/50 p-3 space-y-2'
                    >
                      <div className='flex items-start justify-between gap-2 min-w-0'>
                        <div className='min-w-0'>
                          <div className='text-sm font-mono text-white truncate'>{c.domain}</div>
                          <div className='flex flex-wrap items-center gap-1.5 mt-1'>
                            <span
                              className={`text-[10px] px-1.5 py-0.5 rounded-lg ${
                                PROVIDER_CLR[c.provider] ?? 'bg-gray-800 text-gray-400'
                              }`}
                            >
                              {c.provider}
                            </span>
                            {c.auto_renew && (
                              <span className='text-[9px] text-blue-400'>auto</span>
                            )}
                            <span
                              className={`text-[10px] ${
                                c.status === 'active' ? 'text-emerald-400' : 'text-red-400'
                              }`}
                            >
                              {c.status}
                            </span>
                          </div>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-lg shrink-0 ${exp.cls}`}>
                          {exp.label}
                        </span>
                      </div>
                      <div className='flex items-center justify-between gap-2'>
                        <span className='text-[11px] text-gray-500 truncate'>
                          {c.managed_by ?? '—'}
                        </span>
                        <Button
                          variant='secondary'
                          size='sm'
                          disabled={c.status !== 'active'}
                          loading={toNpmMut.isPending && toNpmMut.variables === c.id}
                          onClick={() => toNpmMut.mutate(c.id)}
                        >
                          {t('certmgr.npm')}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Import modal */}
      <Modal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title={t('certmgr.importovatCertifikatZeSouboru')}
        size='lg'
        footer={
          <>
            <Button variant='secondary' onClick={() => setImportOpen(false)}>{t('certmgr.zrusit')}</Button>
            <Button
              variant='primary'
              loading={importMut.isPending}
              disabled={!importCert || !importKey}
              onClick={() => importMut.mutate()}
            >
              Importovat
            </Button>
          </>
        }
      >
        <div className='space-y-3 text-sm'>
          <p className='text-gray-400'>
            {t(
              'certmgr.importPemHint',
              'Vlož PEM obsah, nebo vyber soubory. Cert + private key povinné, intermediate chain volitelný. Server validuje (parse + privkey ↔ cert match).',
            )}
          </p>
          <div>
            <label className='flex items-center justify-between mb-1'>
              <span className='text-xs text-gray-400'>
                {t('certmgr.certifikatCertPemFullchain')} <span className='text-red-400'>*</span>
              </span>
              <input
                type='file'
                accept='.pem,.crt,.cer,.txt'
                onChange={(e) => handleImportFile(e, setImportCert)}
                className='text-[10px] file:text-[10px] file:px-2 file:py-0.5 file:bg-gray-800 file:border-0 file:rounded file:text-gray-300 file:cursor-pointer'
              />
            </label>
            <textarea
              value={importCert}
              onChange={(e) => setImportCert(e.target.value)}
              rows={4}
              className='w-full bg-gray-900 border border-gray-700 rounded-lg px-2 py-1.5 text-[11px] font-mono text-gray-200'
              placeholder={t('certmgr.beginCertificate1010')}
            />
          </div>
          <div>
            <label className='flex items-center justify-between mb-1'>
              <span className='text-xs text-gray-400'>
                {t('certmgr.privateKeyPrivkeyPem')} <span className='text-red-400'>*</span>
              </span>
              <input
                type='file'
                accept='.pem,.key,.txt'
                onChange={(e) => handleImportFile(e, setImportKey)}
                className='text-[10px] file:text-[10px] file:px-2 file:py-0.5 file:bg-gray-800 file:border-0 file:rounded file:text-gray-300 file:cursor-pointer'
              />
            </label>
            <textarea
              value={importKey}
              onChange={(e) => setImportKey(e.target.value)}
              rows={4}
              className='w-full bg-gray-900 border border-gray-700 rounded-lg px-2 py-1.5 text-[11px] font-mono text-gray-200'
              placeholder={t('certmgr.beginPrivateKey10')}
            />
          </div>
          <div>
            <label className='flex items-center justify-between mb-1'>
              <span className='text-xs text-gray-400'>
                {t('certmgr.intermediateChainChainPem')}
              </span>
              <input
                type='file'
                accept='.pem,.crt,.cer,.txt'
                onChange={(e) => handleImportFile(e, setImportChain)}
                className='text-[10px] file:text-[10px] file:px-2 file:py-0.5 file:bg-gray-800 file:border-0 file:rounded file:text-gray-300 file:cursor-pointer'
              />
            </label>
            <textarea
              value={importChain}
              onChange={(e) => setImportChain(e.target.value)}
              rows={3}
              className='w-full bg-gray-900 border border-gray-700 rounded-lg px-2 py-1.5 text-[11px] font-mono text-gray-200'
              placeholder={t('certmgr.volitelneIntermediateCertS')}
            />
          </div>
          <div className='grid grid-cols-2 gap-3'>
            <div>
              <label className='text-xs text-gray-400 block mb-1'>{t('certmgr.labelVolitelne')}</label>
              <input
                type='text'
                value={importLabel}
                onChange={(e) => setImportLabel(e.target.value)}
                className='w-full bg-gray-900 border border-gray-700 rounded-lg px-2 py-1.5 text-sm text-white'
                placeholder={t('certmgr.naprGodaddy2026')}
              />
            </div>
            <label className='flex items-center gap-2 text-sm text-gray-300 cursor-pointer pt-5'>
              <input
                type='checkbox'
                checked={importOverwrite}
                onChange={(e) => setImportOverwrite(e.target.checked)}
              />
              <span>{t('certmgr.prepsatExistujiciProStejnou')}</span>
            </label>
          </div>
          <div className='text-[10px] text-gray-500'>
            {t('certmgr.certSeUloziDo')} <code>/etc/acm-ca/imported/&lt;domain&gt;/</code>
            {t('certmgr.importDbNote', ' + DB záznam s provider=')}
            <code>imported</code>, auto_renew=false.
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ─── Trust Anchors Tab ─────────────────────────────────────────────────────

interface CaDownloadResult {
  ok: boolean;
  filename?: string;
  content_type?: string;
  base64?: string;
  error?: string;
}

function decodeBase64Text(b64: string): string {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

async function fetchCaDownload(
  artifact: 'root_crt' | 'root_der' | 'intermediate_crt' | 'chain_pem' | 'fingerprints_json',
  scope: EffectiveScope,
): Promise<CaDownloadResult> {
  return await callWidgetTool<CaDownloadResult>('certmgr.ca_download', { artifact }, scope, { silent: true });
}

async function downloadCaArtifact(
  artifact: 'root_crt' | 'root_der' | 'intermediate_crt' | 'chain_pem',
  scope: EffectiveScope,
  onError: (msg: string) => void,
) {
  try {
    const r = await fetchCaDownload(artifact, scope);
    if (!r.ok || !r.base64 || !r.filename) {
      onError(r.error ?? i18n.t('certmgr.downloadFailed', 'Stažení selhalo'));
      return;
    }
    downloadBase64(r.base64, r.filename, r.content_type);
  } catch (e) {
    onError((e as Error).message ?? i18n.t('certmgr.downloadFailed', 'Stažení selhalo'));
  }
}

function TrustAnchorsTab() {
  const { t } = useTranslation();
  const fpQ = useQuery<{ root: string | null; intermediate: string | null; node: string }>({
    queryKey: ['certmgr-fingerprints-local'],
    queryFn: async () => {
      const r = await fetchCaDownload('fingerprints_json', _widgetScope);
      if (!r.ok || !r.base64) return { root: null, intermediate: null, node: 'unknown' };
      return JSON.parse(decodeBase64Text(r.base64)) as { root: string | null; intermediate: string | null; node: string };
    },
  });

  // Stejná normalizace jako NodePicker — NE raw mesh.peers_list pod starým
  // queryKeyem (cache collision → .filter is not a function).
  const peersQ = useMeshPeers({ includeLocal: false });
  const peerNodes = Array.isArray(peersQ.data) ? peersQ.data : [];

  const peerFps = useQuery<Record<string, { root: string | null; intermediate: string | null }>>({
    queryKey: ['certmgr-fingerprints-peers', peerNodes.map((n) => n.name).join(',')],
    enabled: peerNodes.length > 0,
    queryFn: async () => {
      const out: Record<string, { root: string | null; intermediate: string | null }> = {};
      for (const n of peerNodes) {
        // enabled=false → nefetchuj; jinak zkus fingerprints (alive se v peers_list nevrací)
        if (n.enabled === false) {
          out[n.name] = { root: null, intermediate: null };
          continue;
        }
        try {
          const r = await fetchCaDownload('fingerprints_json', { node: n.name, identity: null });
          if (!r.ok || !r.base64) {
            out[n.name] = { root: null, intermediate: null };
            continue;
          }
          const j = JSON.parse(decodeBase64Text(r.base64)) as { root?: string; intermediate?: string };
          out[n.name] = { root: j.root ?? null, intermediate: j.intermediate ?? null };
        } catch {
          out[n.name] = { root: null, intermediate: null };
        }
      }
      return out;
    },
  });

  const linuxSnippet = `# Linux (Debian/Ubuntu)
curl -fsSL /apiext/certmgr/ca/root.crt | sudo tee /usr/local/share/ca-certificates/acm-root-ca.crt > /dev/null
sudo update-ca-certificates`;

  const macSnippet = `# macOS (system keychain)
curl -fsSL /apiext/certmgr/ca/root.crt -o /tmp/acm-root-ca.crt
sudo security add-trusted-cert -d -r trustRoot -k /Library/Keychains/System.keychain /tmp/acm-root-ca.crt`;

  const winSnippet = `# Windows (PowerShell, Administrator)
curl.exe -fsSL /apiext/certmgr/ca/root.der -o $env:TEMP\\acm-root-ca.der
Import-Certificate -FilePath "$env:TEMP\\acm-root-ca.der" -CertStoreLocation Cert:\\LocalMachine\\Root`;

  return (
    <div className='space-y-4'>
      <Card>
        <CardHeader>
          <CardTitle icon={Download} className='text-sm'>
            {t('certmgr.lokalniNod', 'Lokální nod ({{node}})', { node: fpQ.data?.node ?? '…' })}
          </CardTitle>
          <CardDescription>
            {t('certmgr.rootCaJeSdileny')}
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-3'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
            <div className='border border-gray-800 rounded-xl p-3 bg-gray-900/40'>
              <div className='flex items-center justify-between mb-2'>
                <div className='font-medium text-sm text-gray-100'>{t('certmgr.rootCa')}</div>
                <Badge variant={fpQ.data?.root ? 'success' : 'error'}>
                  {fpQ.data?.root ? t('certmgr.available', 'k dispozici') : t('certmgr.missing', 'chybí')}
                </Badge>
              </div>
              <div className='text-xs text-gray-500 font-mono break-all mb-3'>
                {fpQ.data?.root ?? '—'}
              </div>
              <div className='flex gap-2'>
                <Button
                  variant='primary'
                  size='sm'
                  onClick={() => downloadCaArtifact('root_crt', _widgetScope, (msg) => toast.error(msg))}
                >
                  <Download size={14} /> PEM
                </Button>
                <Button
                  variant='secondary'
                  size='sm'
                  onClick={() => downloadCaArtifact('root_der', _widgetScope, (msg) => toast.error(msg))}
                >
                  <Download size={14} /> DER
                </Button>
              </div>
            </div>
            <div className='border border-gray-800 rounded-xl p-3 bg-gray-900/40'>
              <div className='flex items-center justify-between mb-2'>
                <div className='font-medium text-sm text-gray-100'>{t('certmgr.intermediateTentoNod')}</div>
                <Badge variant={fpQ.data?.intermediate ? 'success' : 'error'}>
                  {fpQ.data?.intermediate ? t('certmgr.available', 'k dispozici') : t('certmgr.missing', 'chybí')}
                </Badge>
              </div>
              <div className='text-xs text-gray-500 font-mono break-all mb-3'>
                {fpQ.data?.intermediate ?? '—'}
              </div>
              <div className='flex gap-2'>
                <Button
                  variant='primary'
                  size='sm'
                  onClick={() => downloadCaArtifact('intermediate_crt', _widgetScope, (msg) => toast.error(msg))}
                >
                  <Download size={14} /> PEM
                </Button>
                <Button
                  variant='secondary'
                  size='sm'
                  onClick={() => downloadCaArtifact('chain_pem', _widgetScope, (msg) => toast.error(msg))}
                >
                  <Download size={14} /> Chain
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle icon={Server} className='text-sm'>{t('certmgr.intermediateNapricMesh')}</CardTitle>
          <CardDescription>
            {t('certmgr.kazdyNodMaVlastni')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {peersQ.isLoading
            ? <div className='text-sm text-gray-500'>{t('certmgr.nacitamPeerNody')}</div>
            : peersQ.isError
            ? (
              <div className='text-sm text-red-400'>
                {(peersQ.error as Error)?.message ?? t('certmgr.panelLoadFailed', 'Načtení peerů selhalo')}
              </div>
            )
            : peerNodes.length === 0
            ? <div className='text-sm text-gray-500'>{t('certmgr.zadnePeerNody', 'Žádné peer nody v mesh.')}</div>
            : (
              <table className='w-full text-sm'>
                <thead>
                  <tr className='border-b border-gray-800 text-gray-500'>
                    <th className='text-left p-2 font-medium'>Node</th>
                    <th className='text-left p-2 font-medium'>Stav</th>
                    <th className='text-left p-2 font-medium'>{t('certmgr.rootFp')}</th>
                    <th className='text-left p-2 font-medium'>{t('certmgr.intermediateFp')}</th>
                    <th className='text-left p-2 font-medium'>Akce</th>
                  </tr>
                </thead>
                <tbody>
                  {peerNodes.map((n) => {
                    const fp = peerFps.data?.[n.name];
                    const online = n.enabled !== false;
                    return (
                      <tr key={n.name} className='border-b border-gray-800/60 text-gray-300'>
                        <td className='p-2 font-mono text-gray-100'>{n.label || n.name}</td>
                        <td className='p-2'>
                          <StatusDot status={online ? 'running' : 'stopped'} />
                        </td>
                        <td className='p-2 font-mono text-xs break-all text-gray-400'>{fp?.root ?? '—'}</td>
                        <td className='p-2 font-mono text-xs break-all text-gray-400'>{fp?.intermediate ?? '—'}</td>
                        <td className='p-2'>
                          {online && (
                            <Button
                              variant='ghost'
                              size='sm'
                              onClick={() =>
                                downloadCaArtifact(
                                  'intermediate_crt',
                                  { node: n.name, identity: null },
                                  (msg) => toast.error(msg),
                                )}
                            >
                              <Download size={14} />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle icon={Terminal} className='text-sm'>{t('certmgr.instalaceDoTrustStoru')}</CardTitle>
          <CardDescription>
            {t('certmgr.rootCaStaciInstalovat')}
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-3'>
          <div>
            <div className='text-xs font-medium text-gray-400 mb-1'>{t('certmgr.linuxDebianUbuntu')}</div>
            <pre className='text-xs bg-gray-950/80 text-gray-300 border border-gray-800 p-2 rounded-lg font-mono whitespace-pre-wrap break-all'>
{linuxSnippet}
            </pre>
          </div>
          <div>
            <div className='text-xs font-medium text-gray-400 mb-1'>macOS</div>
            <pre className='text-xs bg-gray-950/80 text-gray-300 border border-gray-800 p-2 rounded-lg font-mono whitespace-pre-wrap break-all'>
{macSnippet}
            </pre>
          </div>
          <div>
            <div className='text-xs font-medium text-gray-400 mb-1'>Windows</div>
            <pre className='text-xs bg-gray-950/80 text-gray-300 border border-gray-800 p-2 rounded-lg font-mono whitespace-pre-wrap break-all'>
{winSnippet}
            </pre>
          </div>
          <div className='text-xs text-gray-500'>
            {t('certmgr.iosAndroidStahni')} <code>root.der</code> v browseru → Profile install → trust manually
            v <em>{t('certmgr.settingsAboutCertificateTrust')}</em>.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


export function OverviewPanel({ scope }: WidgetRenderProps) {
  setWidgetScope(scope ?? { node: null, identity: null });
  const { t } = useTranslation();
  const { data: status, refetch: refetchStatus } = useQuery<CAStatus>({
    queryKey: ['certmgr-status', scope?.node ?? 'local'],
    queryFn: async () => {
      // allSettled: timeout/chyba jedné karty nesmí shodit celý přehled
      // (root_ca status na uzlu bez root image visel a UI ukázalo error
      // i pro zdravou intermediate — Filip 2026-09-02).
      const [rootR, interR, listR] = await Promise.allSettled([
        callMcp<{ ok: boolean; initialized?: boolean; present?: boolean }>('certmgr.root_ca', { action: 'status' }),
        callMcp<{ ok: boolean; ready?: boolean; expiresAt?: string }>('certmgr.intermediate', { action: 'status' }),
        callMcp<{ ok: boolean; certs?: Cert[] }>('certmgr.node', { action: 'list' }),
      ]);
      const root = rootR.status === 'fulfilled' ? rootR.value : { ok: false } as { ok: boolean; initialized?: boolean; present?: boolean };
      const inter = interR.status === 'fulfilled' ? interR.value : { ok: false } as { ok: boolean; ready?: boolean; expiresAt?: string };
      const list = listR.status === 'fulfilled' ? listR.value : { ok: false, certs: [] as Cert[] };
      const certs = Array.isArray(list.certs) ? list.certs : [];
      const now = Date.now();
      return {
        ok: true,
        rootCa: {
          ready: !!root.initialized,
          absent: root.present === false,
          dir: '/opt/acm/ca-root',
          image: 'acm-root-ca',
        },
        intermediateCa: { ready: !!inter.ready, dir: '/etc/acm-ca', expiresAt: inter.expiresAt },
        certs: {
          total: certs.length,
          active: certs.filter((c) => !c.revoked_at && new Date(c.expires_at).getTime() > now).length,
          revoked: certs.filter((c) => !!c.revoked_at).length,
          expired: certs.filter((c) => !c.revoked_at && new Date(c.expires_at).getTime() <= now).length,
        },
      };
    },
    staleTime: 30_000,
  });
  const { data: certsData } = useQuery<{ ok: boolean; certs: Cert[] }>({
    queryKey: ['certmgr-certs', scope?.node ?? 'local'],
    queryFn: () => callMcp<{ ok: boolean; certs: Cert[] }>('certmgr.node', { action: 'list' }),
    staleTime: 60_000,
  });
  const certs = Array.isArray(certsData?.certs) ? certsData.certs : [];

  return (
    <div className='space-y-6'>
      <div className='flex justify-end'>
        <Button variant='secondary' size='sm' onClick={() => refetchStatus()} icon={<RefreshCw size={14} />}>
          Refresh
        </Button>
      </div>
      <StatsGrid columns={4}>
        <StatCard label={t('certmgr.aktivni')} value={status?.certs.active ?? 0} variant='success' icon={CheckCircle2} />
        <StatCard label={t('certmgr.celkem', 'Celkem')} value={status?.certs.total ?? 0} icon={FileText} />
        <StatCard label={t('certmgr.revokovane')} value={status?.certs.revoked ?? 0} variant='error' icon={AlertCircle} />
        <StatCard label={t('certmgr.expirovane')} value={status?.certs.expired ?? 0} variant='warning' icon={Clock} />
      </StatsGrid>
      <div className='grid gap-6 md:grid-cols-2'>
        <CAStatusCard
          label={t('certmgr.rootCa')}
          status={status?.rootCa.ready ?? false}
          absent={(status?.rootCa as { absent?: boolean } | undefined)?.absent}
          dir='/opt/acm/ca-root'
        />
        <CAStatusCard label={t('certmgr.intermediateCa')} status={status?.intermediateCa.ready ?? false} dir='/etc/acm-ca' expiresAt={status?.intermediateCa.expiresAt} />
      </div>
      <div className='grid gap-6 md:grid-cols-3 mt-6'>
        <Card>
          <CardHeader><CardTitle icon={Key} className='text-sm'>{t('certmgr.rootCa')}</CardTitle></CardHeader>
          <CardContent className='space-y-3'>
            <div className='flex items-center gap-2 text-sm'>
              <StatusDot status={status?.rootCa.ready ? 'running' : 'offline'} />
              <span className='text-gray-400'>{status?.rootCa.ready ? t('certmgr.runningDocker', 'Běží (Docker)') : t('certmgr.notInstalled', 'Nenainstalována')}</span>
            </div>
            <code className='block text-[11px] font-mono bg-gray-800/50 p-2 rounded text-gray-400'>acm-root-ca</code>
            <div className='text-xs text-gray-500'>{t('certmgr.network')} <span className='text-gray-400'>{t('certmgr.noneIzolovano')}</span></div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle icon={Lock} className='text-sm'>{t('certmgr.intermediateCa')}</CardTitle></CardHeader>
          <CardContent className='space-y-3'>
            <div className='flex items-center gap-2 text-sm'>
              <StatusDot status={status?.intermediateCa.ready ? 'running' : 'offline'} />
              <span className='text-gray-400'>{status?.intermediateCa.ready ? t('certmgr.statusActive', 'Aktivní') : t('certmgr.notInstalled', 'Nenainstalována')}</span>
            </div>
            <div className='text-xs text-gray-500'>{t('certmgr.platforma')} <span className='text-gray-400'>{t('certmgr.linuxOpenssl')}</span></div>
            <div className='text-xs text-gray-500'>{t('certmgr.platnost')} <span className='text-gray-400'>{t('certmgr.5Let')}</span></div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle icon={Server} className='text-sm'>{t('certmgr.nodeCerts')}</CardTitle></CardHeader>
          <CardContent className='space-y-3'>
            <div className='flex items-center gap-2 text-sm'>
              <StatusDot status={certs?.length > 0 ? 'active' : 'offline'} />
              <span className='text-gray-400'>{certs?.length ?? 0} {t('certmgr.certifikatu', 'certifikátů')}</span>
            </div>
            <div className='text-xs text-gray-500'>{t('certmgr.platnost')} <span className='text-gray-400'>{t('certmgr.90DniDefault')}</span></div>
            <div className='text-xs text-gray-500'>{t('certmgr.evidence')} <span className='text-gray-400'>PostgreSQL</span></div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export function CertificatesPanel({ scope }: WidgetRenderProps) {
  setWidgetScope(scope ?? { node: null, identity: null });
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [serialToRevoke, setSerialToRevoke] = useState('');
  const { data: certsData } = useQuery<{ ok: boolean; certs: Cert[] }>({
    queryKey: ['certmgr-certs', scope?.node ?? 'local'],
    queryFn: () => callMcp<{ ok: boolean; certs: Cert[] }>('certmgr.node', { action: 'list' }),
    staleTime: 60_000,
  });
  const certs = Array.isArray(certsData?.certs) ? certsData!.certs! : [];
  const revokeMutation = useMutation({
    mutationFn: async (serial: string) =>
      callMcp<{ ok: boolean }>('certmgr.node', {
        action: 'revoke',
        serial,
        revoke_reason: t('certmgr.userRevokeReason', 'Uživatelská revokace'),
      }),
    onSuccess: () => {
      toast.success(t('certmgr.certifikatRevokovan'));
      qc.invalidateQueries({ queryKey: ['certmgr-certs'] });
      setShowRevokeModal(false);
      setSerialToRevoke('');
    },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <div className='space-y-4'>
      <CertTable certs={certs} onRevoke={(serial) => { setSerialToRevoke(serial); setShowRevokeModal(true); }} />
      <Modal
        open={showRevokeModal}
        onClose={() => setShowRevokeModal(false)}
        title={t('certmgr.revokaceCertifikatu')}
        footer={
          <>
            <Button variant='secondary' onClick={() => setShowRevokeModal(false)}>{t('certmgr.zrusit')}</Button>
            <Button variant='danger' loading={revokeMutation.isPending} onClick={() => serialToRevoke && revokeMutation.mutate(serialToRevoke)}>
              Revokovat
            </Button>
          </>
        }
      >
        <div className='space-y-4'>
          <div className='p-4 bg-red-950/20 border border-red-500/20 rounded-xl'>
            <div className='flex items-start gap-3'>
              <AlertCircle className='text-red-400 flex-shrink-0 mt-0.5' size={18} />
              <div>
                <p className='text-sm text-red-300 font-medium'>{t('certmgr.opravduChceteRevokovatTento')}</p>
                <p className='text-xs text-red-400/80 mt-1'>{t('certmgr.revokaceJeTrvalaA')}</p>
              </div>
            </div>
          </div>
          {serialToRevoke && (
            <div className='space-y-2'>
              <label className='text-xs font-medium text-gray-500 uppercase tracking-wider'>{t('certmgr.serialCertifikatu')}</label>
              <code className='block w-full font-mono text-sm bg-gray-950 p-3 rounded border border-gray-800 text-gray-300'>{serialToRevoke}</code>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

export function CertStorePanel({ scope }: WidgetRenderProps) {
  setWidgetScope(scope ?? { node: null, identity: null });
  return <CertStoreTab />;
}

export function TrustAnchorsPanel({ scope }: WidgetRenderProps) {
  setWidgetScope(scope ?? { node: null, identity: null });
  return <TrustAnchorsTab />;
}

export function LePanel({ scope }: WidgetRenderProps) {
  setWidgetScope(scope ?? { node: null, identity: null });
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>
        <CardTitle icon={Globe} className='text-sm'>{t('certmgr.letSEncrypt')}</CardTitle>
        <CardDescription>{t('certmgr.spravaLetSEncrypt')}</CardDescription>
      </CardHeader>
      <CardContent className='space-y-6'>
        <div className='bg-blue-900/20 border border-blue-500/20 rounded-xl p-4'>
          <p className='text-sm text-blue-300'>
            <span className='font-semibold'>{t('certmgr.poznamka')}</span>{' '}
            {t('certmgr.leManagedViaMcp', "Let's Encrypt certifikáty jsou spravovány přes MCP nástroj")}{' '}
            <code className='font-mono bg-blue-950/30 px-1.5 py-0.5 rounded text-blue-200'>certmgr_le</code>
            {t('certmgr.tatoStrankaBudeV')}
          </p>
        </div>
        <div className='grid gap-4 md:grid-cols-2'>
          <div className='p-4 rounded-xl border border-gray-800 bg-gray-900/20'>
            <div className='flex items-center gap-2 mb-2'>
              <Terminal size={16} className='text-gray-400' />
              <span className='text-sm font-medium text-white'>{t('certmgr.mcpNastroj')}</span>
            </div>
            <code className='block text-xs font-mono text-gray-400 bg-gray-950 p-2 rounded'>
              certmgr_le issue domain=example.com email=admin@example.com
            </code>
          </div>
          <div className='p-4 rounded-xl border border-gray-800 bg-gray-900/20'>
            <div className='flex items-center gap-2 mb-2'>
              <Terminal size={16} className='text-gray-400' />
              <span className='text-sm font-medium text-white'>{t('certmgr.apiEndpoint')}</span>
            </div>
            <code className='block text-xs font-mono text-gray-400 bg-gray-950 p-2 rounded'>
              GET /apiext/certmgr/certs?type_filter=le
            </code>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function DnsProvidersWidgetPanel({ scope }: WidgetRenderProps) {
  setWidgetScope(scope ?? { node: null, identity: null });
  return <DnsProvidersPanel />;
}

export function DockerCaPanel({ scope }: WidgetRenderProps) {
  setWidgetScope(scope ?? { node: null, identity: null });
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>
        <CardTitle icon={Server} className='text-sm'>{t('certmgr.rootCaDocker')}</CardTitle>
        <CardDescription>{t('certmgr.rootCaBeziV')}</CardDescription>
      </CardHeader>
      <CardContent className='space-y-6'>
        <div className='grid gap-4 md:grid-cols-3'>
          <div className='p-4 rounded-xl border border-gray-800 bg-gray-900/20'>
            <div className='flex items-center gap-2 mb-2'>
              <Server size={16} className='text-blue-400' />
              <span className='text-sm font-medium text-white'>Kontejner</span>
            </div>
            <div className='text-xs text-gray-500'>{t('certmgr.nazev')}</div>
            <div className='font-mono text-sm text-white mt-0.5'>acm-root-ca</div>
          </div>
          <div className='p-4 rounded-xl border border-gray-800 bg-gray-900/20'>
            <div className='flex items-center gap-2 mb-2'>
              <Shield size={16} className='text-emerald-400' />
              <span className='text-sm font-medium text-white'>{t('certmgr.bezpecnost')}</span>
            </div>
            <div className='text-xs text-gray-500'>{t('certmgr.sit')}</div>
            <div className='font-mono text-sm text-emerald-400 mt-0.5'>--network none</div>
          </div>
          <div className='p-4 rounded-xl border border-gray-800 bg-gray-900/20'>
            <div className='flex items-center gap-2 mb-2'>
              <FileText size={16} className='text-amber-400' />
              <span className='text-sm font-medium text-white'>Volume</span>
            </div>
            <div className='text-xs text-gray-500'>Host</div>
            <div className='font-mono text-sm text-amber-400 mt-0.5 truncate'>/opt/acm/ca-root:/ca</div>
          </div>
        </div>
        <div className='bg-amber-900/20 border border-amber-500/20 rounded-xl p-4'>
          <p className='text-sm text-amber-300'>
            <span className='font-semibold'>{t('certmgr.bezpecnostniUpozorneni')}</span>{' '}
            {t('certmgr.rootCaPrivateKey', 'Root CA private key je uložen šifrovaně v /opt/acm/ca-root/root.key. Passphrase je\n            kritická — ztráta passphrase znamená ztrátu přístupu k Root CA!')}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
